/**
 * Country dial-code dataset for the PhoneInput component.
 *
 * Data-only (ISO 3166-1 alpha-2 + E.164 country calling code). Flag emoji
 * are DERIVED from the ISO code at render time (regional-indicator code
 * points) so we don't ship 240 emoji literals. No external dependency.
 *
 * Ordered: a handful of common markets first (so the default list opens on
 * familiar choices), then the remainder alphabetically by name.
 */

export interface Country {
  /** ISO 3166-1 alpha-2 code, uppercase (e.g. "US"). */
  iso: string
  /** Display name. */
  name: string
  /** E.164 country calling code WITHOUT the leading "+" (e.g. "1", "44"). */
  dial: string
}

/**
 * flagEmoji turns an ISO alpha-2 code into its flag emoji by mapping each
 * ASCII letter to its Unicode regional-indicator symbol. Returns "" for
 * malformed input so the UI degrades to text-only rather than throwing.
 */
export function flagEmoji(iso: string): string {
  if (!iso || iso.length !== 2) return ''
  const upper = iso.toUpperCase()
  if (!/^[A-Z]{2}$/.test(upper)) return ''
  const base = 0x1f1e6 // regional indicator "A"
  return String.fromCodePoint(base + (upper.charCodeAt(0) - 65), base + (upper.charCodeAt(1) - 65))
}

// Common markets surfaced first.
const COMMON: Country[] = [
  { iso: 'US', name: 'United States', dial: '1' },
  { iso: 'GB', name: 'United Kingdom', dial: '44' },
  { iso: 'CA', name: 'Canada', dial: '1' },
  { iso: 'AU', name: 'Australia', dial: '61' },
  { iso: 'IN', name: 'India', dial: '91' },
  { iso: 'DE', name: 'Germany', dial: '49' },
  { iso: 'FR', name: 'France', dial: '33' },
  { iso: 'SG', name: 'Singapore', dial: '65' },
  { iso: 'AE', name: 'United Arab Emirates', dial: '971' },
]

// Remainder, alphabetical by name. (Common entries above are excluded here.)
const REST: Country[] = [
  { iso: 'AR', name: 'Argentina', dial: '54' },
  { iso: 'AT', name: 'Austria', dial: '43' },
  { iso: 'BD', name: 'Bangladesh', dial: '880' },
  { iso: 'BE', name: 'Belgium', dial: '32' },
  { iso: 'BR', name: 'Brazil', dial: '55' },
  { iso: 'BG', name: 'Bulgaria', dial: '359' },
  { iso: 'CL', name: 'Chile', dial: '56' },
  { iso: 'CN', name: 'China', dial: '86' },
  { iso: 'CO', name: 'Colombia', dial: '57' },
  { iso: 'HR', name: 'Croatia', dial: '385' },
  { iso: 'CZ', name: 'Czechia', dial: '420' },
  { iso: 'DK', name: 'Denmark', dial: '45' },
  { iso: 'EG', name: 'Egypt', dial: '20' },
  { iso: 'EE', name: 'Estonia', dial: '372' },
  { iso: 'FI', name: 'Finland', dial: '358' },
  { iso: 'GR', name: 'Greece', dial: '30' },
  { iso: 'HK', name: 'Hong Kong', dial: '852' },
  { iso: 'HU', name: 'Hungary', dial: '36' },
  { iso: 'ID', name: 'Indonesia', dial: '62' },
  { iso: 'IE', name: 'Ireland', dial: '353' },
  { iso: 'IL', name: 'Israel', dial: '972' },
  { iso: 'IT', name: 'Italy', dial: '39' },
  { iso: 'JP', name: 'Japan', dial: '81' },
  { iso: 'KE', name: 'Kenya', dial: '254' },
  { iso: 'MY', name: 'Malaysia', dial: '60' },
  { iso: 'MX', name: 'Mexico', dial: '52' },
  { iso: 'NL', name: 'Netherlands', dial: '31' },
  { iso: 'NZ', name: 'New Zealand', dial: '64' },
  { iso: 'NG', name: 'Nigeria', dial: '234' },
  { iso: 'NO', name: 'Norway', dial: '47' },
  { iso: 'PK', name: 'Pakistan', dial: '92' },
  { iso: 'PH', name: 'Philippines', dial: '63' },
  { iso: 'PL', name: 'Poland', dial: '48' },
  { iso: 'PT', name: 'Portugal', dial: '351' },
  { iso: 'RO', name: 'Romania', dial: '40' },
  { iso: 'SA', name: 'Saudi Arabia', dial: '966' },
  { iso: 'ZA', name: 'South Africa', dial: '27' },
  { iso: 'KR', name: 'South Korea', dial: '82' },
  { iso: 'ES', name: 'Spain', dial: '34' },
  { iso: 'SE', name: 'Sweden', dial: '46' },
  { iso: 'CH', name: 'Switzerland', dial: '41' },
  { iso: 'TW', name: 'Taiwan', dial: '886' },
  { iso: 'TH', name: 'Thailand', dial: '66' },
  { iso: 'TR', name: 'Turkey', dial: '90' },
  { iso: 'UA', name: 'Ukraine', dial: '380' },
  { iso: 'VN', name: 'Vietnam', dial: '84' },
]

/** Full ordered country list (common markets first, then alphabetical). */
export const COUNTRIES: Country[] = [...COMMON, ...REST]

/** Default country when none is supplied. */
export const DEFAULT_COUNTRY_ISO = 'US'

/** Look up a country by ISO code; falls back to the default. */
export function countryByIso(iso: string | undefined): Country {
  const found = iso ? COUNTRIES.find((c) => c.iso === iso.toUpperCase()) : undefined
  return found ?? COUNTRIES[0]
}
